import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ImageViewerHost } from '@orbind/ui'
import { useQueryClient } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import { Link, Outlet, useLocation, useNavigate } from 'react-router'
import { AvisosDoChat } from '../features/chat/AvisosDoChat.tsx'
import { useNaoLidasDoChat } from '../features/chat/useChat.ts'
import { VideochamadaProvider } from '../features/chat/Videochamada.tsx'
import { ChamadaDoEscritorioProvider } from '../features/office/chamada.tsx'
import { api } from '../lib/api.ts'
import { useI18n } from '../lib/i18n.tsx'
import { sessionAvatarSrc, useSession } from '../lib/session.tsx'
import { useAccessTracker } from '../lib/access-tracker.ts'
import { isDesktop } from '../lib/platform.ts'
import { desktopApps } from '../features/app/desktop.ts'
import { ConviteParaODesktop } from '../features/app/ConviteParaODesktop.tsx'
import {
  idDaExtensao,
  rotaDaExtensao,
  rotuloDaExtensao,
  useExtensoesInstaladas,
} from '../features/plugin/useExtensoes.ts'
import { esquecerDispensaDoDesktop } from '../features/app/convite-desktop.ts'
import { AvisosDeNotificacao } from '../features/notification/AvisosDeNotificacao.tsx'
import { useNotificacoes } from '../features/notification/useNotificacoes.ts'
import { AppsStage, useAppsRuntime } from '../features/app/runtime.tsx'
import { ChangePasswordModal } from '../features/auth/ChangePasswordModal.tsx'
import { WorkspaceSection } from '../features/workspace/WorkspaceMenu.tsx'
import { WorkspaceBar, useCurrentWorkspace, workspaceLogoUrl, siglaDoWorkspace } from '../features/workspace/WorkspaceBar.tsx'
import { useTemaEfetivo } from './theme.tsx'
import { BoardBackgroundContext, BoardTelaCheiaContext, type BoardBackground } from './board-background.tsx'
import {
  ExplorerProvider,
  PANE_COLAPSA_ABAIXO_DE,
  PANE_LARGURA_PADRAO,
  PANE_MAX,
  PANE_MIN,
  usePaneSize,
  type ExplorerView,
  type ExplorerRailItem,
} from './explorer.tsx'
import { OrbindLogo } from './OrbindLogo.tsx'
import { PreferencesModal } from './PreferencesModal.tsx'
import { ModuleGate, useModuleGates } from '../lib/module-gate.tsx'
import { usePreferenciaDeLayout } from '../lib/layout-prefs.ts'
import { MembersPanel, SeletorDeStatus } from '../features/presence/MembersPanel.tsx'
import { PainelDeNotificacoes } from './PainelDeNotificacoes.tsx'
import { useExplorerProprio } from './explorer-proprio.ts'
import { PainelDoEscritorio } from './PainelDoEscritorio.tsx'
import { PresenceProvider } from '../features/presence/PresenceProvider.tsx'
import { VoltarAoTrabalho } from '../features/presence/VoltarAoTrabalho.tsx'
import { MODULES, activeModule, type ModuleEntry } from './modules.ts'
import { CONTEUDO_NA_RAIZ, caminhoDeVolta, useCompacto } from './compacto.ts'
import { AbasDoCelular, FolhaMais } from './NavegacaoMovel.tsx'
import { useAvisoDeNovaVersao } from './update-check.ts'
import { BarraDaJanela, useBarraDaJanela } from './BarraDaJanela.tsx'
import './shell.css'

/**
 * Shell global.
 *
 * Segue o protótipo (`docs/design-slices/_shell.html:26-28,44,509,518`): tres
 * colunas — rail de 64px, explorer redimensionavel e `<main>`. **Nao ha barra
 * superior**: a busca, o titulo do modulo, o botao criar e os filtros ficam no
 * topo do explorer, e o sino e o avatar ancoram no rodape do rail.
 *
 * Abaixo de 900px o explorer vira uma gaveta sobre o conteudo, porque as tres
 * colunas nao cabem.
 */
/**
 * Ordem dos modulos no rail, personalizada por arraste. Persiste no navegador
 * (localStorage): e preferencia de layout, nao dado de negocio — igual a
 * largura do explorer. Modulo que ainda nao esta na ordem salva (feature nova)
 * entra na posicao padrao dele.
 */
const RAIL_ORDER_KEY = 'orbind.rail-order'

function ordemSalvaDoRail(): string[] {
  try {
    const bruto = localStorage.getItem(RAIL_ORDER_KEY)
    const lido = bruto ? (JSON.parse(bruto) as unknown) : null
    return Array.isArray(lido) ? lido.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

export function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useSession()
  useAccessTracker()
  const { t } = useI18n()
  // Desktop: avisa quando um deploy novo chega com a janela aberta.
  useAvisoDeNovaVersao()

  const current = activeModule(location.pathname)

  /**
   * Celular e tablet (SPEC-20260930-0131): explorador e conteúdo se revezam na
   * tela — sem nada escolhido, a lista ocupa a tela; escolhido algo, o
   * conteúdo ocupa a tela e a barra do topo ganha o "voltar". A navegação
   * entre módulos fica nas abas do pé (SPEC-20261007-0909). Nada disso existe
   * no computador, nem com a janela estreita (ver compacto.ts).
   */
  const compacto = useCompacto()
  // barra do topo (SPEC-20261104-0202 e 1202): em toda plataforma fora do
  // compacto; os controles da janela so no Desktop sem a decoracao nativa
  const barra = useBarraDaJanela()
  const comBarra = barra.mostrar && !compacto
  const explorerProprio = useExplorerProprio()
  const queryClient = useQueryClient()
  const [menuAberto, setMenuAberto] = useState(false)
  const fecharMais = useCallback(() => setMenuAberto(false), [])
  const [listaAberta, setListaAberta] = useState(false)
  useEffect(() => {
    setMenuAberto(false)
    setListaAberta(false)
  }, [location.pathname])
  const temSelecao =
    !!current &&
    location.pathname.startsWith(current.route) &&
    location.pathname.replace(/\/+$/, '') !== current.route

  /**
   * Painel de membros: aberto/fechado e largura. Ambos na CONTA e nao no
   * navegador (2026-09-12) — a preferencia e da pessoa: vale no desktop e na
   * web, e nao vaza entre colegas que dividem o mesmo computador.
   */
  const [membrosAberto, setMembrosAberto] = usePreferenciaDeLayout<boolean>('membrosAberto', false)
  // painel do sino (SPEC-20261104-1219): sessao, nao preferencia — abre no
  // clique e fecha ao clicar fora, como o de Membros; um fecha o outro
  const [notificacoesAberto, setNotificacoesAberto] = useState(false)
  // escritorio em painel suspenso (SPEC-20261104-1438): mesmo molde
  const [escritorioAberto, setEscritorioAberto] = useState(false)
  // "Reduzir" na tela cheia do escritorio volta para o painel (SPEC-20261104-1520)
  useEffect(() => {
    const abrir = () => setEscritorioAberto(true)
    window.addEventListener('orbind:escritorio-painel', abrir)
    return () => window.removeEventListener('orbind:escritorio-painel', abrir)
  }, [])
  useEffect(() => {
    if (!escritorioAberto) return
    const aoClicar = (e: MouseEvent) => {
      const alvo = e.target as Element | null
      if (!alvo?.closest) return
      if (alvo.closest('.epanel, .ob-modal, .ob-overlay, .ob-anchored, .esc__menu, [aria-label="Escritório virtual"]')) return
      setEscritorioAberto(false)
    }
    document.addEventListener('mousedown', aoClicar, true)
    return () => document.removeEventListener('mousedown', aoClicar, true)
  }, [escritorioAberto])
  useEffect(() => {
    if (!notificacoesAberto) return
    const aoClicar = (e: MouseEvent) => {
      const alvo = e.target as Element | null
      if (!alvo?.closest) return
      if (alvo.closest('.npanel, .ob-modal, .ob-overlay, .ob-anchored, [data-notificacoes-toggle]')) return
      setNotificacoesAberto(false)
    }
    document.addEventListener('mousedown', aoClicar, true)
    return () => document.removeEventListener('mousedown', aoClicar, true)
  }, [notificacoesAberto])
  const [membrosLargura, setMembrosLargura] = usePreferenciaDeLayout<number>('membrosLargura', 248)
  /**
   * Clique FORA recolhe o painel (pedido 2026-09-12). Ficam de fora da regra o
   * proprio painel, o botao do rail que o abre (senao abrir e fechar no mesmo
   * clique) e as sobreposicoes ancoradas que ele abre — o perfil e o menu de
   * uma pessoa vivem em portal, fora da arvore do painel.
   */
  useEffect(() => {
    if (!membrosAberto) return
    const aoClicar = (e: MouseEvent) => {
      const alvo = e.target as Element | null
      if (!alvo?.closest) return
      if (alvo.closest('.mpanel, .ob-anchored, [data-membros-toggle]')) return
      setMembrosAberto(false)
    }
    // captura: uma tela que pare a propagacao nao pode prender o painel aberto
    document.addEventListener('mousedown', aoClicar, true)
    return () => document.removeEventListener('mousedown', aoClicar, true)
  }, [membrosAberto, setMembrosAberto])

  // Estado do explorer — mora aqui, a tela so le (ver explorer.tsx).
  const [search, setSearch] = useState('')
  const [chip, setChip] = useState('')
  const [view, setView] = useState<ExplorerView>('list')
  const [body, setBody] = useState<HTMLElement | null>(null)
  const [acoesDoExplorer, setAcoesDoExplorer] = useState<HTMLElement | null>(null)
  const [footer, setFooter] = useState<string | null>(null)
  // O handler fica embrulhado num objeto de proposito: `setState(fn)` seria
  // lido pelo React como updater funcional e guardaria o retorno da acao em
  // vez da propria acao.
  const [create, setCreate] = useState<{ fn: (() => void) | null }>({ fn: null })
  const setCreateHandler = useCallback((acao: (() => void) | null) => setCreate({ fn: acao }), [])
  const [hasTreeView, setHasTreeView] = useState(false)
  const [railItems, setRailItems] = useState<ExplorerRailItem[]>([])
  const [profileOpen, setProfileOpen] = useState(false)
  const [prefsOpen, setPrefsOpen] = useState(false)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)

  // Rail personalizavel: ordem por arraste + qual icone esta sendo arrastado.
  const [railOrder, setRailOrder] = useState<string[]>(ordemSalvaDoRail)
  const [railDragId, setRailDragId] = useState<string | null>(null)

  // Modulo fora do plano do workspace some do rail (SPEC-20260814-1333).
  const { moduleEnabled } = useModuleGates()

  /**
   * Telas de plugin no rail (SPEC-...-plugin-tela-no-rail).
   *
   * Entram como módulo sintético, e não numa lista à parte, para herdarem tudo
   * que o rail já faz: ordem arrastável, ícone ativo, rolagem. O gate de
   * permissão vale igual — `moduleEnabled` recebe o id `plugin:<key>:<ext>`,
   * então um administrador pode negar a tela de um plugin como nega a de um
   * módulo nativo.
   */
  const telasDePlugin = useExtensoesInstaladas('rail-screen', moduleEnabled('plugins')).data ?? []

  const railModules = useMemo(() => {
    const dePlugin: ModuleEntry[] = telasDePlugin.map((e) => ({
      id: idDaExtensao(e),
      labelKey: '',
      fallbackLabel: rotuloDaExtensao(e),
      icon: e.config.icon ?? 'fas fa-plug',
      route: rotaDaExtensao(e),
      specId: 'plugin',
      status: 'ready',
      summary: `Plugin ${e.pluginKey}`,
      searchPlaceholder: '',
      createLabel: '',
      chips: [],
      // a tela do plugin traz o próprio conteúdo; o explorer do shell não tem
      // o que mostrar por ela
      ownExplorer: true,
    }))
    const visiveis = [...MODULES, ...dePlugin].filter(
      (module) =>
        !module.railHidden &&
        moduleEnabled(module.id) &&
        // com a barra do topo o Escritorio mora nela (SPEC-20261104-1235) e a
        // Administracao mora no menu do workspace (SPEC-20261104-1422)
        !(comBarra && (module.id === 'office' || module.id === 'admin')),
    )
    if (railOrder.length === 0) return visiveis
    const posicao = new Map(railOrder.map((id, indice) => [id, indice]))
    // desconhecidos preservam a ordem padrao entre si, depois dos ordenados
    return [...visiveis].sort((a, b) => {
      const pa = posicao.get(a.id) ?? railOrder.length + visiveis.indexOf(a)
      const pb = posicao.get(b.id) ?? railOrder.length + visiveis.indexOf(b)
      return pa - pb
    })
  }, [railOrder, moduleEnabled, telasDePlugin, comBarra])

  /** Reordena AO VIVO enquanto o arraste passa por cima de outro icone. */
  const reordenarRail = useCallback(
    (sobreId: string) => {
      if (!railDragId || railDragId === sobreId) return
      const ids = railModules.map((module) => module.id).filter((id) => id !== railDragId)
      ids.splice(ids.indexOf(sobreId), 0, railDragId)
      setRailOrder(ids)
    },
    [railDragId, railModules],
  )

  useEffect(() => {
    if (railOrder.length === 0) return
    try {
      localStorage.setItem(RAIL_ORDER_KEY, JSON.stringify(railOrder))
    } catch {
      // sem storage (modo privado etc.): a ordem vale so ate recarregar
    }
  }, [railOrder])

  // Setas de "tem mais icone" acima/abaixo da area rolavel do rail.
  const railScrollRef = useRef<HTMLDivElement | null>(null)
  const [railHints, setRailHints] = useState({ up: false, down: false })
  const atualizarRailHints = useCallback(() => {
    const el = railScrollRef.current
    if (!el) return
    setRailHints({
      up: el.scrollTop > 1,
      down: el.scrollTop + el.clientHeight < el.scrollHeight - 1,
    })
  }, [])
  useEffect(() => {
    atualizarRailHints()
    const el = railScrollRef.current
    if (!el) return
    const observer = new ResizeObserver(atualizarRailHints)
    observer.observe(el)
    return () => observer.disconnect()
  }, [atualizarRailHints, railModules])

  const { width, setWidth, collapsed, setCollapsed } = usePaneSize()
  const [boardBg, setBoardBg] = useState<BoardBackground | null>(null)
  // No Black o fundo do quadro NÃO é pintado (SPEC-20261104-1631, pedido do
  // Cristian): o preto fosco é o visual; imagem/cor do quadro ficam guardadas
  // e voltam ao sair do Black.
  const temaBlack = useTemaEfetivo() === 'black'
  const fundoDoQuadro = temaBlack ? null : boardBg
  // tela cheia das aplicacoes: some rail + explorer (2026-08-28)
  const { telaCheia } = useAppsRuntime()
  // tela cheia do QUADRO (SPEC-20260915-2116): mesmo efeito, pedido pelo quadro
  const [quadroCheio, setQuadroCheio] = useState(false)

  // Selo do sino: notificações não lidas de verdade (SPEC-20260924-2111). Só
  // consulta quando o plano inclui o módulo — é o mesmo gate que esconde o sino.
  const naoLidas = useNotificacoes(moduleEnabled('notifications')).data?.naoLidas ?? 0
  // Selo do ícone da Comunicação: não lidas de todas as conversas (pedido 2026-09-14).
  const naoLidasDoChat = useNaoLidasDoChat(moduleEnabled('channels')).data?.naoLidas ?? 0
  const workspace = useCurrentWorkspace()

  // Trocar de modulo zera busca e volta o chip para o primeiro do modulo.
  useEffect(() => {
    setSearch('')
    setChip(current?.chips[0] ?? '')
  }, [current?.id, current?.chips])

  // O fundo do quadro vale enquanto se esta em Quadros.
  useEffect(() => {
    if (!location.pathname.startsWith('/kanbans')) setBoardBg(null)
  }, [location.pathname])

  // Navegar fecha o popover de perfil, senao ele fica sobre a tela nova.
  useEffect(() => setProfileOpen(false), [location.pathname])

  // Arraste do divisor. Abaixo de PANE_COLAPSA_ABAIXO_DE o painel recolhe em
  // vez de continuar encolhendo (protótipo `_shell.html:2070-2086`).
  const arrastando = useRef(false)
  /**
   * Onde a borda esquerda do explorer estaria para o ponto pego da faixa ficar
   * sob o ponteiro: largura = clientX - origem. Antes era clientX - 64 (rail
   * fixo), e a faixa saltava para fora do ponteiro ao começar o arraste.
   */
  const origemDoArraste = useRef(0)
  const larguraAtual = useRef({ width, collapsed })
  larguraAtual.current = { width, collapsed }
  const comecarArraste = (clientX: number) => {
    const { width: atual, collapsed: recolhido } = larguraAtual.current
    origemDoArraste.current = clientX - (recolhido ? 0 : atual)
  }
  useEffect(() => {
    const mover = (event: MouseEvent) => {
      if (!arrastando.current) return
      const bruto = event.clientX - origemDoArraste.current
      if (bruto < PANE_COLAPSA_ABAIXO_DE) {
        setCollapsed(true)
        return
      }
      setCollapsed(false)
      setWidth(Math.min(PANE_MAX, Math.max(PANE_MIN, bruto)))
    }
    const soltar = () => {
      if (!arrastando.current) return
      arrastando.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', mover)
    window.addEventListener('mouseup', soltar)
    return () => {
      window.removeEventListener('mousemove', mover)
      window.removeEventListener('mouseup', soltar)
    }
  }, [setCollapsed, setWidth])

  const clearFilters = useCallback(() => {
    setSearch('')
    setChip(current?.chips[0] ?? '')
  }, [current?.chips])

  const explorerCtx = useMemo(
    () => ({
      search,
      chip,
      view,
      body,
      acoes: acoesDoExplorer,
      clearFilters,
      setFooter,
      setCreateHandler,
      setHasTreeView,
      railItems,
      setRailItems,
    }),
    [search, chip, view, body, acoesDoExplorer, clearFilters, setCreateHandler, railItems],
  )

  const label = (module: (typeof MODULES)[number]) => {
    const traduzido = t(module.labelKey)
    return traduzido === module.labelKey ? module.fallbackLabel : traduzido
  }

  const iniciais = user.name
    .split(' ')
    .map((parte) => parte[0])
    .slice(0, 2)
    .join('')

  const aoCriar = useCallback(() => {
    create.fn?.()
  }, [create])

  // Itens de Trabalho e Quadros trazem o proprio explorer (ver modules.ts) —
  // o shell nao desenha o dele nesses modulos para nao duplicar o painel.
  const explorerModule = current && !current.ownExplorer ? current : null

  // modo do celular: 'lista' (explorador do shell na tela), 'lista-propria'
  // (Itens/Quadros com explorador próprio) ou 'conteudo'
  const conteudoNaRaiz = !!current && CONTEUDO_NA_RAIZ.has(current.id)
  const modoCompacto = !compacto
    ? null
    : temSelecao || conteudoNaRaiz || !current
      ? 'conteudo'
      : explorerModule
        ? 'lista'
        : current.ownExplorer
          ? 'lista-propria'
          : 'conteudo'
  // `menuAberto` é a folha do "Mais" desde a barra de abas
  // (SPEC-20261007-0909): o rail não abre mais em gaveta no celular
  const classesCompacto = modoCompacto
    ? ` shell--compacto shell--c-${modoCompacto}${listaAberta ? ' shell--lista-aberta' : ''}`
    : ''
  const avatarDaPessoa = sessionAvatarSrc(user) ? <img src={sessionAvatarSrc(user)!} alt="" /> : iniciais

  return (
    // O provedor envolve o shell inteiro, e nao so o painel: a conexao de
    // presenca precisa viver mesmo com a coluna fechada, senao fechar a coluna
    // deixaria a pessoa invisivel para os colegas.
    <PresenceProvider>
    {/* Videochamada: o telefone toca em qualquer tela, não só na Comunicação. */}
    <VideochamadaProvider>
    {/* A chamada das salas do Escritório segue viva em qualquer tela (SPEC-20261010-1557). */}
    <ChamadaDoEscritorioProvider>
    {/* Avisos de mensagem e menção em qualquer tela do Orbind, não só na Comunicação. */}
    <AvisosDoChat />
    {/* Atribuição de Item de Trabalho: cartão no app ou aviso do sistema. */}
    <AvisosDeNotificacao />
    {/* Quem nunca entrou pelo app instalado é convidado a instalar. */}
    <ConviteParaODesktop />
    {/* Status pendurado de ontem: ausente volta sozinho, offline pergunta. */}
    <VoltarAoTrabalho />
    {/* O fundo do quadro cobre o shell inteiro; rail e explorer ganham pelicula
        por cima em vez de esconder a imagem. */}
    <div
      className={`ob-root shell${fundoDoQuadro ? ' shell--bg' : ''}${fundoDoQuadro?.dark ? ' shell--bgdark' : ''}${telaCheia || quadroCheio ? ' shell--fullapp' : ''}${classesCompacto}${comBarra ? ' shell--com-barra' : ''}`}
      style={fundoDoQuadro?.style}
    >
      {comBarra ? (
        <BarraDaJanela
          // "Orbind — Funcionalidade", nesta ordem (pedido do Cristian, SPEC-20261104-1412)
          titulo={current ? `Orbind — ${label(current)}` : 'Orbind'}
          comControles={barra.comControles}
          // tela com painel proprio (Trabalho/Quadros): o botao comanda ESSE
          // painel (SPEC-20261104-1413); senao, o explorer do shell
          temExplorer={Boolean(explorerModule) || explorerProprio !== null}
          explorerRecolhido={explorerProprio ? explorerProprio.recolhido : collapsed}
          aoAlternarExplorer={explorerProprio ? explorerProprio.alternar : () => setCollapsed(!collapsed)}
          notificacoes={
            moduleEnabled('notifications')
              ? {
                  naoLidas,
                  ativa: notificacoesAberto,
                  aoAbrir: () => {
                    setNotificacoesAberto(!notificacoesAberto)
                    if (!notificacoesAberto) setMembrosAberto(false)
                  },
                }
              : null
          }
          membrosAberto={membrosAberto}
          aoAlternarMembros={() => {
            setMembrosAberto(!membrosAberto)
            if (!membrosAberto) setNotificacoesAberto(false)
          }}
          workspace={<WorkspaceBar />}
          escritorio={
            moduleEnabled('office')
              ? {
                  ativo: escritorioAberto || location.pathname.startsWith('/office'),
                  // na propria tela do escritorio o botao nao abre painel por cima
                  aoAbrir: () => {
                    if (location.pathname.startsWith('/office')) return
                    setEscritorioAberto(!escritorioAberto)
                    if (!escritorioAberto) {
                      setNotificacoesAberto(false)
                      setMembrosAberto(false)
                    }
                  },
                }
              : null
          }
          novaJanela={isDesktop() ? () => void desktopApps.novaJanela() : null}
          sistema={[
            { rotulo: 'Conta', icone: 'fas fa-id-card', aoClicar: () => navigate('/account') },
            { rotulo: 'Portal do desenvolvedor', icone: 'fas fa-code', aoClicar: () => navigate('/developer') },
            ...(user.isSuperAdmin ? [{ rotulo: 'Console', icone: 'fas fa-terminal', aoClicar: () => navigate('/console') }] : []),
          ]}
        />
      ) : null}
      {compacto ? (
        /*
          Barra do topo do celular: voltar, o nome da tela e o sino. A
          navegação entre módulos mora na barra de abas do pé
          (SPEC-20261007-0909-layout-mobile-barra-e-inicio) — o ☰ que abria o
          rail em gaveta saiu.
        */
        <header className={`shell__barra${temSelecao && current ? '' : ' shell__barra--sem-voltar'}`}>
          {temSelecao && current ? (
            <button
              type="button"
              className="shell__barra-btn"
              aria-label="Voltar"
              onClick={() => navigate(caminhoDeVolta(location.pathname, current.route))}
            >
              <i className="fas fa-arrow-left" aria-hidden />
            </button>
          ) : null}
          {/* fora de um módulo (o Início), o nome do workspace: diz onde se está */}
          <span className="shell__barra-titulo">{current ? label(current) : (workspace?.name ?? 'Orbind')}</span>
          {conteudoNaRaiz && explorerModule && !temSelecao ? (
            <button
              type="button"
              className="shell__barra-btn"
              aria-label={listaAberta ? 'Fechar a lista' : 'Abrir a lista'}
              aria-expanded={listaAberta}
              onClick={() => setListaAberta(!listaAberta)}
            >
              <i className="fas fa-list" aria-hidden />
            </button>
          ) : null}
          {moduleEnabled('notifications') ? (
            <button
              type="button"
              className={`shell__barra-btn shell__barra-sino${location.pathname.startsWith('/notifications') ? ' shell__barra-btn--on' : ''}`}
              aria-label={naoLidas > 0 ? `Notificações — ${naoLidas} não lidas` : 'Notificações'}
              onClick={() => navigate('/notifications')}
            >
              <i className="fas fa-bell" aria-hidden />
              {naoLidas > 0 ? <span className="shell__badge">{naoLidas > 99 ? '99+' : naoLidas}</span> : null}
            </button>
          ) : null}
        </header>
      ) : null}
      {/* o fundo escuro só vale para a lista em gaveta; o menu empurra a tela */}
      {compacto && listaAberta ? (
        <div
          className="shell__scrim"
          aria-hidden
          onClick={() => {
            setMenuAberto(false)
            setListaAberta(false)
          }}
        />
      ) : null}
      <aside className="shell__rail" aria-label="Módulos">
        {/* O logo do WORKSPACE assume o lugar da marca; sem logo, mostra a SIGLA
            do workspace (pedido 2026-08-26) para sempre indicar onde se está.
            Só sem workspace nenhum a marca Orbind aparece. */}
        <Link
          to="/home"
          className="shell__logo"
          aria-label={workspace ? `${workspace.name} — início` : 'Orbind — início'}
          title={workspace ? `${workspace.name} — início` : 'Orbind — o ambiente onde todo o trabalho se conecta'}
        >
          {workspace?.hasLogo ? (
            <img className="shell__logo-img" src={workspaceLogoUrl(workspace)} alt="" />
          ) : workspace ? (
            <span className="shell__logo-sigla" aria-hidden>{siglaDoWorkspace(workspace.name)}</span>
          ) : (
            <OrbindLogo size={38} />
          )}
        </Link>

        {/* Os modulos rolam com a roda do mouse quando nao cabem; sino e
            avatar continuam ancorados fora da area rolavel. Arrastar um icone
            sobre outro troca as posicoes ao vivo. */}
        <div className="shell__rail-scroll">
          <div className="shell__rail-modules" role="list" ref={railScrollRef} onScroll={atualizarRailHints}>
            {railModules.map((module) => (
            <button
              key={module.id}
              type="button"
              draggable
              className={`shell__rail-item${current?.id === module.id ? ' shell__rail-item--active' : ''}${
                railDragId === module.id ? ' shell__rail-item--dragging' : ''
              }${module.id === 'channels' && naoLidasDoChat > 0 ? ' shell__rail-item--contador' : ''}`}
              aria-label={
                module.id === 'channels' && naoLidasDoChat > 0
                  ? `${label(module)} — ${naoLidasDoChat} ${naoLidasDoChat === 1 ? 'mensagem não lida' : 'mensagens não lidas'}`
                  : label(module)
              }
              aria-current={current?.id === module.id ? 'page' : undefined}
              title={`${label(module)} — ${module.summary}`}
              onClick={() => navigate(module.route)}
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = 'move'
                setRailDragId(module.id)
              }}
              onDragOver={(event) => {
                event.preventDefault()
                reordenarRail(module.id)
              }}
              onDragEnd={() => setRailDragId(null)}
            >
              <i className={module.icon} aria-hidden />
              {module.id === 'channels' && naoLidasDoChat > 0 ? (
                <span className="shell__badge" aria-hidden>
                  {naoLidasDoChat > 99 ? '99+' : naoLidasDoChat}
                </span>
              ) : null}
              </button>
            ))}
          </div>
          {railHints.up ? (
            <div className="shell__rail-hint shell__rail-hint--up" aria-hidden>
              <i className="fas fa-chevron-up" />
            </div>
          ) : null}
          {railHints.down ? (
            <div className="shell__rail-hint shell__rail-hint--down" aria-hidden>
              <i className="fas fa-chevron-down" />
            </div>
          ) : null}
        </div>

        {/* NOVA JANELA do Orbind (2026-08-31, so no desktop): abre outra
            instancia completa do app para trabalhar em duas coisas ao mesmo
            tempo. A barra de titulo com o X e NATIVA do Windows — nao da para
            por um icone nela sem reescrever a titlebar; este e o ponto mais
            proximo dela dentro do app. */}
        {isDesktop() && !comBarra ? (
          <button
            type="button"
            className="shell__rail-item"
            aria-label="Abrir nova janela do Orbind"
            title="Nova janela do Orbind"
            onClick={() => void desktopApps.novaJanela()}
          >
            <i className="fas fa-window-restore" aria-hidden />
          </button>
        ) : null}

        {/* Assistente com IA (SPEC-20261104-2229): a tela diz se a pessoa foi
            liberada, como o Conectar ao Claude — a porta fica sempre à vista. */}
        <button
          type="button"
          className={`shell__rail-item${location.pathname.startsWith('/assistant') ? ' shell__rail-item--active' : ''}`}
          aria-label="Orbind AI"
          title="Orbind AI"
          onClick={() => navigate('/assistant')}
        >
          <i className="fas fa-magic" aria-hidden />
        </button>

        {/* sino some quando o plano nao inclui Notificacoes (SPEC-20260814-1427)
            e, com a barra do topo, mora nela e nao aqui (SPEC-20261104-1202) */}
        {moduleEnabled('notifications') && !comBarra ? (
          <button
            type="button"
            className={`shell__rail-item${location.pathname.startsWith('/notifications') ? ' shell__rail-item--active' : ''}`}
            aria-label={naoLidas > 0 ? `Notificações — ${naoLidas} não lidas` : 'Notificações'}
            title="Notificações"
            onClick={() => navigate('/notifications')}
          >
            <i className="fas fa-bell" aria-hidden />
            {naoLidas > 0 ? <span className="shell__badge">{naoLidas}</span> : null}
          </button>
        ) : null}

        {/* Membros/presenca: o gesto de abrir mora no RAIL, logo acima da foto
            (pedido 2026-09-12) — junto das demais acoes pessoais, e nao num
            botao flutuante sobre a tela. Com a barra da janela do Desktop ele
            migra para a barra, antes de Minimizar (SPEC-20261104-1150). */}
        {comBarra ? null : (
          <button
            type="button"
            className={`shell__rail-item${membrosAberto ? ' shell__rail-item--active' : ''}`}
            aria-label={membrosAberto ? 'Fechar painel de membros' : 'Membros e status'}
            aria-expanded={membrosAberto}
            data-membros-toggle
            title="Membros e status"
            onClick={() => setMembrosAberto(!membrosAberto)}
          >
            <i className="fas fa-user-friends" aria-hidden />
          </button>
        )}

        <div className="shell__profile">
          <button
            type="button"
            className="shell__avatar"
            aria-label={`Perfil de ${user.name}`}
            aria-expanded={profileOpen}
            title={user.name}
            onClick={() => setProfileOpen((aberto) => !aberto)}
          >
            {sessionAvatarSrc(user) ? <img src={sessionAvatarSrc(user)!} alt="" /> : iniciais}
          </button>

          {profileOpen ? (
            // portal para o body: o rail ganha backdrop-filter com fundo de
            // quadro (cria stacking context) e prenderia o menu atras do painel
            createPortal(
              <>
                <div className="shell__popover-scrim" onClick={() => setProfileOpen(false)} aria-hidden />
              <div
                className={`shell__popover${compacto ? ' shell__popover--folha' : ''}`}
                role="menu"
                aria-label="Perfil e preferências"
              >
                <div className="shell__popover-head">
                  <span className="shell__popover-name">{user.name}</span>
                  <span className="shell__popover-mail">{user.email}</span>
                </div>
                {/* Conta, Portal e Console: com a barra do topo moram no menu do
                    sistema dela (SPEC-20261104-1444); aqui so no compacto */}
                {comBarra ? null : (
                <button
                  type="button"
                  role="menuitem"
                  className="shell__popover-item"
                  onClick={() => {
                    setProfileOpen(false)
                    navigate('/account')
                  }}
                >
                  <i className="fas fa-id-card" aria-hidden />
                  Conta
                </button>
                )}
                {/* Portal de quem ESCREVE plugin. Aberto a qualquer pessoa: o
                    cadastro é que passa por aprovação, não o acesso à página —
                    esconder a porta faria ninguém descobrir que pode publicar. */}
                {comBarra ? null : (
                <button
                  type="button"
                  role="menuitem"
                  className="shell__popover-item"
                  onClick={() => {
                    setProfileOpen(false)
                    navigate('/developer')
                  }}
                >
                  <i className="fas fa-code" aria-hidden />
                  Portal do desenvolvedor
                </button>
                )}
                {user.isSuperAdmin && !comBarra ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="shell__popover-item shell__popover-item--console"
                    onClick={() => {
                      setProfileOpen(false)
                      navigate('/console')
                    }}
                  >
                    <i className="fas fa-terminal" aria-hidden />
                    Console
                  </button>
                ) : null}
                {/* com a barra do topo o workspace mora nela (SPEC-20261104-1442):
                    o menu do perfil fica so com o que e da pessoa */}
                {comBarra ? null : (
                  <WorkspaceSection
                    onCriar={() => {
                      setProfileOpen(false)
                      navigate('/workspace/new')
                    }}
                  />
                )}
                <button
                  type="button"
                  role="menuitem"
                  className="shell__popover-item"
                  onClick={() => {
                    setProfileOpen(false)
                    navigate('/profile')
                  }}
                >
                  <i className="fas fa-user-circle" aria-hidden />
                  Meu perfil
                </button>
                {/* Conector do Claude (SPEC-20261103-1818): a tela diz se a
                    pessoa foi liberada — a porta fica sempre à vista. */}
                <button
                  type="button"
                  role="menuitem"
                  className="shell__popover-item"
                  onClick={() => {
                    setProfileOpen(false)
                    navigate('/claude')
                  }}
                >
                  <i className="fas fa-robot" aria-hidden />
                  Conectar ao Claude
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="shell__popover-item"
                  onClick={() => {
                    setProfileOpen(false)
                    navigate('/assistant')
                  }}
                >
                  <i className="fas fa-magic" aria-hidden />
                  Orbind AI
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="shell__popover-item"
                  onClick={() => {
                    setProfileOpen(false)
                    setPrefsOpen(true)
                  }}
                >
                  <i className="fas fa-sliders-h" aria-hidden />
                  Preferências
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="shell__popover-item"
                  onClick={() => {
                    setProfileOpen(false)
                    navigate('/status')
                  }}
                >
                  <i className="far fa-dot-circle" aria-hidden />
                  Atualizar meu status
                </button>
                {/* Escolha de presenca (SPEC-20260912-0145). Fica aqui, e nao
                    numa tela, porque trocar status e gesto de dois cliques. */}
                <SeletorDeStatus onEscolher={() => setProfileOpen(false)} />
                <button
                  type="button"
                  role="menuitem"
                  className="shell__popover-item"
                  onClick={() => {
                    setProfileOpen(false)
                    setChangePasswordOpen(true)
                  }}
                >
                  <i className="fas fa-key" aria-hidden />
                  Alterar senha
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="shell__popover-item shell__popover-item--danger"
                  onClick={() => {
                    esquecerDispensaDoDesktop()
                    api.logout()
                    // o cache era da pessoa que saiu
                    queryClient.clear()
                    navigate('/login', { replace: true })
                  }}
                >
                  <i className="fas fa-sign-out-alt" aria-hidden />
                  Sair
                </button>
              </div>
              </>,
              document.body,
            )
          ) : null}
        </div>
      </aside>

      {explorerModule && (!collapsed || compacto) ? (
        <section className="shell__explorer" style={{ width }} aria-label={`Explorador de ${label(explorerModule)}`}>
          {/* com a barra do topo o workspace mora nela (SPEC-20261104-1224) */}
          {comBarra ? null : <WorkspaceBar />}
          <div className="shell__explorer-head">
            <h1 className="shell__explorer-title">{label(explorerModule)}</h1>

            {hasTreeView ? (
              <div className="shell__viewtoggle" role="group" aria-label="Modo de exibição">
                <button
                  type="button"
                  className={view === 'list' ? 'is-on' : undefined}
                  aria-pressed={view === 'list'}
                  title="Lista"
                  onClick={() => setView('list')}
                >
                  <i className="fas fa-list" aria-hidden />
                </button>
                <button
                  type="button"
                  className={view === 'tree' ? 'is-on' : undefined}
                  aria-pressed={view === 'tree'}
                  title="Árvore"
                  onClick={() => setView('tree')}
                >
                  <i className="fas fa-sitemap" aria-hidden />
                </button>
              </div>
            ) : null}

            {/* Vaga para botoes extras da tela (ver ExplorerHeaderActions). */}
            <div className="shell__explorer-acoes" ref={setAcoesDoExplorer} />

            {/*
              Sem ação de criar, o botão SOME (2026-09-16). Antes ele ficava
              apagado, e na Comunicação — onde "Nova conversa" só existe na aba
              Tudo — um `+` morto no topo confundia mais do que ajudava.
            */}
            {create.fn ? (
              <button
                type="button"
                className="shell__explorer-create"
                aria-label={explorerModule.createLabel}
                title={explorerModule.createLabel}
                onClick={aoCriar}
              >
                <i className="fas fa-plus" aria-hidden />
              </button>
            ) : null}
          </div>

          <div className="shell__explorer-search">
            <i className="fas fa-search" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={explorerModule.searchPlaceholder}
              aria-label={explorerModule.searchPlaceholder}
            />
          </div>

          {explorerModule.chips.length > 0 ? (
            <div className="shell__explorer-chips">
              {explorerModule.chips.map((opcao) => (
                <button
                  key={opcao}
                  type="button"
                  className={`shell__chip${chip === opcao ? ' shell__chip--on' : ''}`}
                  aria-pressed={chip === opcao}
                  onClick={() => setChip(opcao)}
                >
                  {opcao}
                </button>
              ))}
            </div>
          ) : null}

          <div className="shell__explorer-body" ref={setBody} />

          {footer ? <div className="shell__explorer-footer">{footer}</div> : null}
        </section>
      ) : null}

      {/* Mini-rail: quando o painel recolhe e o modulo publicou itens, uma
          segunda barra vertical mostra so a imagem/sigla de cada um. Clicar
          troca a selecao; o botao no topo expande o painel de volta.
          (Aplicacoes, pedido 2026-08-26.) */}
      {explorerModule && collapsed && railItems.length > 0 ? (
        <nav className="shell__minirail" aria-label={`${label(explorerModule)} — atalhos`}>
          {/* o botão de expandir saiu (SPEC-20261104-1605): é o ícone da barra da janela */}
          <div className="shell__minirail-list">
            {railItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`shell__minirail-item${item.active ? ' shell__minirail-item--active' : ''}`}
                aria-label={item.label}
                aria-current={item.active ? 'true' : undefined}
                title={item.label}
                onClick={item.onClick}
              >
                {item.content}
              </button>
            ))}
          </div>
        </nav>
      ) : null}

      {explorerModule ? (
        <div
          className="shell__handle"
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(event) => {
            // Sem isto o gesto começa uma seleção de texto na tela ao lado.
            event.preventDefault()
            comecarArraste(event.clientX)
            arrastando.current = true
            document.body.style.cursor = 'col-resize'
            document.body.style.userSelect = 'none'
          }}
          onDoubleClick={() => {
            setWidth(PANE_LARGURA_PADRAO)
            setCollapsed(false)
          }}
        >
          {/* a setinha de recolher saiu (SPEC-20261104-1605): recolher e
              expandir é pelo ícone do Explorer na barra da janela */}
        </div>
      ) : null}

      <main className="shell__main">
        {/* O provedor envolve a tela E o palco: os iframes das aplicacoes
            abertas ficam fora do <Outlet> para sobreviverem a navegacao entre
            modulos (ver features/app/runtime.tsx). */}
        <ExplorerProvider value={explorerCtx}>
          <BoardBackgroundContext.Provider value={setBoardBg}>
          <BoardTelaCheiaContext.Provider value={setQuadroCheio}>
            {/* modulo fora do plano: deep-link cai na tela de bloqueio */}
            <ModuleGate>
              <Outlet />
            </ModuleGate>
          </BoardTelaCheiaContext.Provider>
          </BoardBackgroundContext.Provider>
        </ExplorerProvider>
        <AppsStage />
        {/* clique em imagem de conteudo abre o visualizador com zoom/arrasto */}
        <ImageViewerHost />
      </main>

      {/*
        Sempre montada: fechada, a coluna ENCOLHE para uma faixa estreita em
        vez de sumir (decisao do usuario, 2026-09-12 02:15). Como a faixa faz
        parte do layout, nenhuma tela — nem as futuras — consegue ficar por
        baixo do botao, o que aconteceria com um botao flutuante.
      */}
      <MembersPanel
        aberto={membrosAberto}
        largura={membrosLargura}
        onAlternar={() => setMembrosAberto(!membrosAberto)}
        onLargura={setMembrosLargura}
      />
      {moduleEnabled('notifications') && !compacto ? (
        <PainelDeNotificacoes aberto={notificacoesAberto} onFechar={() => setNotificacoesAberto(false)} />
      ) : null}
      {moduleEnabled('office') && !compacto ? (
        <PainelDoEscritorio aberto={escritorioAberto} onFechar={() => setEscritorioAberto(false)} />
      ) : null}

      {/* celular e tablet: a navegação no pé da tela, e o "Mais" com o resto */}
      {compacto ? (
        <>
          <AbasDoCelular
            modulos={railModules}
            atualId={current?.id ?? null}
            noInicio={location.pathname.startsWith('/home')}
            maisAberto={menuAberto}
            naoLidasDoChat={naoLidasDoChat}
            label={label}
            onIr={(rota) => {
              setMenuAberto(false)
              navigate(rota)
            }}
            onMais={() => setMenuAberto(!menuAberto)}
          />
          <FolhaMais
            aberta={menuAberto}
            modulos={railModules}
            atualId={current?.id ?? null}
            label={label}
            usuario={user}
            avatar={avatarDaPessoa}
            naoLidasDoChat={naoLidasDoChat}
            onIr={(rota) => {
              setMenuAberto(false)
              navigate(rota)
            }}
            onFechar={fecharMais}
            onPerfil={() => {
              setMenuAberto(false)
              setProfileOpen(true)
            }}
            onMembros={() => {
              setMenuAberto(false)
              setMembrosAberto(true)
            }}
          />
        </>
      ) : null}

      {prefsOpen ? <PreferencesModal onClose={() => setPrefsOpen(false)} /> : null}
      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </div>
    </ChamadaDoEscritorioProvider>
    </VideochamadaProvider>
    </PresenceProvider>
  )
}

/** Encerra a sessao e volta ao login. Exposto para o menu de perfil. */
export function useLogout() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  return () => {
    esquecerDispensaDoDesktop()
    api.logout()
    // o cache era da pessoa que saiu
    queryClient.clear()
    navigate('/login', { replace: true })
  }
}
